import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import moment from 'moment';
import { getDateRangeArray, getWeekRangeByMonth } from '@/utils/date';
import { formatDuration, secondsToMinutes } from '@/utils/utils';
import { appConfigService } from '@/providers/services/app/app-config';

@Component({
    name: 'bar-chart-card',
})
export default class BarChartCardComponent extends Vue {

    @Prop()
    public dataList: any;

    @Prop({ default: 'monthly' })
    public frequency!: string;

    @Prop()
    public activeMonth!: string;

    @Prop()
    public activeYear!: string;

    @Prop()
    public nudgeId!: string;

    @Prop()
    public nudgeList!: any;

    @Prop({ default: () => [] })
    public selectNudgeUsageUserIds!: string[];

    public tooltipStyle: any = { toolX: 0, toolY: 0 };

    public tooltipData: any = null;

    public chartWidth = 1060;

    public chartHeight = 420;

    public margin = { top: 20, right: 30, bottom: 60, left: 60 };

    public get startDate() {
        return moment([this.activeYear, parseInt(this.activeMonth, 10) - 1]).toDate();
    }

    public get endDate() {
        return (this.activeMonth === moment().format('MM')
            && this.activeYear === moment().format('YYYY'))
            ? moment().subtract(1, 'day').toDate()
            : moment(this.startDate).endOf('month').toDate();
    }

    public get isCurrentMonthActive() {
        return this.activeMonth === moment().format('MM') && this.activeYear === moment().format('YYYY');
    }

    public get dailyFrequencyLabels() {
        return getDateRangeArray(
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'));
    }

    public get weeklyFrequencyLabels() {
        const tillCurrentWeek = this.activeMonth === moment().format('MM')
            && this.activeYear === moment().format('YYYY');
        return getWeekRangeByMonth(this.activeMonth, this.activeYear, tillCurrentWeek);
    }

    public get cdnUrl() {
        return appConfigService.getCdnUrl();
    }

    public get nudgeInfo() {
        return appConfigService.getNudgeInfo();
    }

    public get isMonthly() {
        return this.frequency === 'monthly';
    }

    @Watch('dataList', { deep: true })
    public async onChangeData() {
        this.drawChart();
    }

    // -----------------------------------------------------------------
    // Hooks

    public mounted() {
        this.drawChart();
    }

    // ------------------------------------------------------------------
    // Methods

    public parseChartData(dataList: any[] = []): any[] {
        const dataSet: any[] = [];

        const labelByFrequency = this.frequency === 'daily'
            ? this.dailyFrequencyLabels
            : this.weeklyFrequencyLabels;

        if (this.isMonthly) {
            // Monthly data processing
            dataList!.forEach((item, index) => {
                const nudgeInfo: any =
                    this.nudgeList.find((nudge: any) => nudge.nudge_id === item.nudge_id) ||
                    this.nudgeInfo[item.nudge_id] || {};
                const iconUrl = (nudgeInfo.thumbnail && nudgeInfo.thumbnail.icon) || nudgeInfo.icon || '';

                dataSet.push({
                    nudgeId: nudgeInfo.name || item.nudge_id,
                    label: item!.nudge_id,
                    pos: index + 1,
                    value: Math.max(0, Number(secondsToMinutes(item.avg_time_spent_seconds || 0))),
                    logo: `${this.cdnUrl}${iconUrl}`,
                    iconUrl,
                    avgTimeSpend: item.avg_time_spent_seconds,
                });
            });
        } else {
            // Daily or Weekly data processing
            labelByFrequency.forEach((frequencyLabel: any, index: number) => {
                const label = this.frequency === 'daily'
                    ? moment(frequencyLabel).format('DD')
                    : frequencyLabel.weekLabel;

                // Find matching data based on index or frequency week
                const matchedData = dataList.find((data: any) =>
                    String(data.label) === String(index + 1) ||
                    String(data.label) === String(frequencyLabel.week),
                ) || {};

                const nudgeIdToMatch = matchedData.nudge_id || this.nudgeId;
                const nudgeInfo =
                    this.nudgeList.find((nudge: any) => nudge.nudge_id === nudgeIdToMatch) ||
                    this.nudgeInfo[nudgeIdToMatch] || {};

                const iconUrl = (nudgeInfo.thumbnail && nudgeInfo.thumbnail.icon) || nudgeInfo.icon || '';

                // Prepare coordinate data
                const coordinateData = matchedData
                    ? {
                        nudgeId: nudgeInfo!.name,
                        label,
                        pos: index + 1,
                        value: Math.max(0, Number(secondsToMinutes(matchedData.avg_time_spent_seconds || 0))),
                        logo: `${this.cdnUrl}${iconUrl}`,
                        iconUrl,
                        avgTimeSpend: matchedData.avg_time_spent_seconds,
                        dateOrWeek: matchedData.label,
                    }
                    : {
                        nudgeId: this.nudgeId ? nudgeInfo!.name : '',
                        label,
                        pos: index + 1,
                        value: 0,
                        logo: this.nudgeId ? `${this.cdnUrl}${iconUrl}` : '',
                        iconUrl,
                        avgTimeSpend: 0,
                    };
                dataSet.push(coordinateData);
            });
        }
        return dataSet;
    }


    public drawChart() {
        // Remove any existing chart before redrawing
        d3.select(this.$el).select('.line-chart-container').select('svg').remove();

        // Parse the chart data
        const chartDataA: any = this.parseChartData(this.dataList || []);

        const width = this.chartWidth - this.margin.left - this.margin.right;
        const height = this.chartHeight - this.margin.top - this.margin.bottom;

        // Set up the SVG container
        const svg = d3
            .select(this.$el)
            .select('.line-chart-container')
            .append('svg')
            .attr('width', this.chartWidth)
            .attr('height', this.chartHeight)
            .append('g')
            .attr(
                'transform',
                `translate(${this.margin.left},${this.margin.top})`,
            );

        if (!chartDataA.length) {
            return this.displayNoDataMessage(svg);
        }

        // X-axis scale (ordinal for bars)
        const x: any = d3
            .scaleBand()
            .domain(chartDataA.map((d: any) => d.pos))
            .range([0, width])
            .padding(0.5);

        const yMaxValue = d3.max(chartDataA, (d: any) => {
            const value = Number(d.value);
            return value < 10 ? 10 : value;
        }) || 10;

        // Y-axis scale
        const y: any = d3
            .scaleLinear()
            .domain([0, yMaxValue <= 10 ? 10 : yMaxValue])
            .range([height, 0]);

        // Draw bars
        svg
            .selectAll('.bar')
            .data(chartDataA)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', (d: any) => x(d.pos))
            .attr('y', (d: any) => y(d.value))
            .attr('width', x.bandwidth())
            .attr('height', (d: any) => height - y(d.value))
            .attr('fill', '#4b8fe2');

        // X-axis
        svg
            .append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickFormat((d: any) => chartDataA[d - 1] ? chartDataA[d - 1].label : ''),
            )
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-family', 'Montserrat');


        if (this.isMonthly) {

            // isMonthly remove the text.
            svg.selectAll('text').remove();

            // Now add the images (logos) for each tick
            svg
                .selectAll('.axis-logo')
                .data(chartDataA)
                .enter()
                .each(function(d: any) {
                    const group = d3.select(this);
                    const xPosition = x(d.pos) + x.bandwidth() / 2;

                    if (!d.iconUrl) {
                        // Append text if logo is not available
                        group
                            .append('text')
                            .attr('x', xPosition)
                            .attr('y', height + 30)
                            .attr('text-anchor', 'middle')
                            .attr('class', 'axis-nudge-id')
                            .text(d.nudgeId)
                            .style('font-size', '10px')
                            .style('font-family', 'Montserrat')
                            .style('fill', '#333');
                    } else {
                        // Append image if logo is available
                        group
                            .append('image')
                            .attr('x', xPosition - 12)
                            .attr('y', height + 10)
                            .attr('width', 20)
                            .attr('height', 20)
                            .attr('href', d.logo)
                            .attr('class', 'axis-logo')
                            .attr('preserveAspectRatio', 'xMidYMid slice');
                    }
                });
        }

        const tickValues = d3.range(0, yMaxValue + 1, yMaxValue / 10);

        // Y-axis
        svg.append('g')
            .call(d3.axisLeft(y).tickValues(tickValues))
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-family', 'Montserrat');

        // Add grid lines for Y-axis
        svg.selectAll('.grid-line')
            .data(tickValues)
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', (d) => y(d))
            .attr('y2', (d) => y(d))
            .attr('stroke', '#00000026')
            .attr('stroke-dasharray', '2 2');

        const tooltipLine = svg
            .append('line')
            .attr('stroke', '#888')
            .attr('stroke-dasharray', '8 8')
            .style('opacity', 0);

        const tooltipCircle = svg
            .append('circle')
            .attr('r', 6)
            .attr('fill', '#3F8DFD')
            .style('opacity', 0);

        // Add axis lable
        this.addAxisLabel(svg, height, width);

        svg
            .selectAll('.dot')
            .data([...chartDataA])
            .enter()
            .append('rect')
            .attr('class', 'dot')
            .attr('x', (d: any) => x(d.pos))
            .attr('y', 0)
            .attr('width', x.bandwidth())
            .attr('height', height)
            .attr('fill', 'transparent')
            .style('cursor', (d: any) => (
                d.avgTimeSpend === undefined ||
                this.selectNudgeUsageUserIds && this.selectNudgeUsageUserIds.length
                ? 'default'
                : 'pointer'))
            .on('mousemove', (d: any) => {
                const centerX = x(d.pos) + x.bandwidth() / 2;
                const centerY = y(yMaxValue);

                // Ensure dayIndex is within bounds
                const dayIndex = d.pos - 1;
                if (dayIndex >= 0 && dayIndex < chartDataA.length && chartDataA[dayIndex].value > 0) {
                    this.tooltipData = { ...chartDataA[dayIndex] };
                    this.tooltipData.avgTimeSpend = formatDuration(chartDataA[dayIndex].avgTimeSpend);

                    // Position the tooltip dynamically
                    const mouseX = centerX + this.margin.left;
                    const mouseY = centerY + this.margin.top;

                    tooltipLine
                        .attr('x1', centerX)
                        .attr('y1', 0)
                        .attr('x2', centerX)
                        .attr('y2', height)
                        .style('opacity', 1);

                    this.tooltipStyle = {
                        toolX: mouseX + 20,
                        toolY: mouseY - 50,
                    };
                }
            })
            .on('mouseleave', () => {
                this.tooltipData = null;
                tooltipLine.style('opacity', 0);
                tooltipCircle.style('opacity', 0);
            }).on('click', (d: any) => {
                if (d.avgTimeSpend === undefined ||
                    this.selectNudgeUsageUserIds &&
                    this.selectNudgeUsageUserIds.length) { return; }
                this.$emit(this.isMonthly ? 'onSelectNudge' : 'onSelectDate', d);
            });
    }

    public displayNoDataMessage(svg: any) {
        // Add "No Data Found" text
        svg.append('text')
            .attr('x', this.chartWidth / 2 - this.margin.left)
            .attr('y', this.chartHeight / 2 - this.margin.top)
            .attr('text-anchor', 'middle')
            .style('font-weight', '600')
            .style('font-family', 'Montserrat')
            .style('text-transform', 'uppercase')
            .style('fill', '#969696')
            .text('No Data Found');
    }

    public addAxisLabel(svg: any, height: number, width: number) {
        const labels = [
            {
                transform: 'translate(-490, 210) rotate(-90)',
                x: 0,
                y: 445,
                text: 'mins',
            },
            {
                transform: 'translate(5, 45) rotate(0)',
                x: width / 2 - 50,
                y: height + 10 ,
                text: this.isMonthly ? 'Nudge Type' : this.frequency === 'daily' ? 'Day' : 'Week',
            },
        ];

        labels.forEach(({ transform, x, y, text }) => {
            svg
                .append('g')
                .attr('transform', transform)
                .append('text')
                .attr('class', 'placeholder')
                .attr('x', x.toString())
                .attr('y', y.toString())
                .style('font-size', '13px')
                .style('font-weight', '600')
                .style('font-family', 'Montserrat')
                .style('text-transform', 'uppercase')
                .style('fill', '#969696')
                .text(text);
        });
    }
}
